import api from './api';
import { translateError } from '../utils/errorTranslations';

/**
 * Obtém dados atuais do mercado
 * @param {boolean} forceRefresh - Se deve forçar atualização dos dados
 * @returns {Promise<object>} Dados de mercado
 */
export const getMarketData = async (forceRefresh = false) => {
  try {
    const { data } = await api.get(`/market-data/?force_refresh=${forceRefresh}`);
    return data;
  } catch (error) {
    console.error('Error fetching market data:', error);
    
    // 🔥 DETECTAR ERRO DE SISTEMA NÃO PRONTO
    const errorMessage = error.response?.data?.error || error.response?.data?.detail || error.message || '';
    const isSystemNotReady = 
      errorMessage.includes('Sistema não está pronto') ||
      errorMessage.includes('SYSTEM READINESS CHECK') ||
      errorMessage.includes('Sistema em inicialização') ||
      error.response?.status === 503;
    
    if (isSystemNotReady) {
      const customError = new Error('Sistema não está pronto. Aguardando inicialização...');
      customError.isSystemNotReady = true;
      customError.originalError = error;
      throw customError;
    }
    
    throw new Error(error.response?.data?.detail || 'Erro ao obter dados de mercado');
  }
};

/**
 * Obtém dados históricos de RSI
 * @param {number} limit - Número de registros a retornar (180 dias = aproximadamente 6 meses)
 * @param {boolean} forceRefresh - Se deve forçar atualização dos dados
 * @returns {Promise<Array>} Dados históricos de RSI
 */
export const getHistoricRSI = async (limit = 180, forceRefresh = false) => {
  try {
    console.log(`Chamando API para dados históricos de RSI: limit=${limit}, forceRefresh=${forceRefresh}`);
    const response = await api.get(`/rsi/historic/?limit=${limit}&force_refresh=${forceRefresh}`);
    
    // Validar resposta da API
    if (!response.data) {
      console.error('Resposta vazia da API de dados históricos de RSI');
      return [];
    }
    
    // A API retorna um objeto com a propriedade 'data' que contém o array
    const apiData = response.data.data || response.data;
    
    if (!Array.isArray(apiData)) {
      console.error('Formato de dados inesperado - esperava array:', apiData);
      return [];
    }
    
    console.log('Dados brutos da API (primeiros 2 itens):', apiData.slice(0, 2));
    
    // Mapear os dados da API para o formato esperado pelo frontend
    const mappedItems = apiData.map(item => {
      if (!item || typeof item !== 'object') {
        console.warn('Item inválido encontrado:', item);
        return null;
      }
      
      // Mapear campos da API para o formato esperado pelo dashboard
      const mappedItem = {
        date: item.Date || item.date, // API usa 'Date' com D maiúsculo
        price: item.Close || item.price, // API usa 'Close' para preço
        rsi: item.RSI_Dynamic_Monthly || item.rsi // API usa 'RSI_Dynamic_Monthly'
      };
      
      // Validar se todos os campos necessários estão presentes
      if (!mappedItem.date) {
        console.warn('Item sem data encontrado:', item);
        return null;
      }
      if (mappedItem.rsi === undefined || mappedItem.rsi === null) {
        console.warn('Item sem RSI encontrado:', item);
        return null;
      }
      if (mappedItem.price === undefined || mappedItem.price === null) {
        console.warn('Item sem preço encontrado:', item);
        return null;
      }
      
      return mappedItem;
    }).filter(item => item !== null); // Remover itens nulos
    
    if (mappedItems.length < apiData.length) {
      console.warn(`Filtrados ${apiData.length - mappedItems.length} itens inválidos de ${apiData.length} totais`);
    }
    
    if (mappedItems.length === 0) {
      console.error('Nenhum item válido nos dados históricos');
      return [];
    }
    
    console.log(`Dados históricos de RSI mapeados com sucesso: ${mappedItems.length} registros`);
    console.log('Amostra dos dados mapeados (primeiros 2 itens):', mappedItems.slice(0, 2));
    return mappedItems;
  } catch (error) {
    console.error('Error fetching historic RSI data:', error);
    console.error('Response status:', error.response?.status);
    console.error('Response data:', error.response?.data);
    console.error('Request config:', error.config);
    
    // Tentar obter os dados usando outra rota
    try {
      console.log('Tentando rota alternativa para histórico de RSI...');
      const { data } = await api.get('/market/history');
      if (Array.isArray(data) && data.length > 0) {
        console.log(`Dados históricos obtidos via rota alternativa: ${data.length} registros`);
        return data;
      }
    } catch (backupError) {
      console.error('Erro na rota alternativa:', backupError);
    }
    
    // Retornar array vazio em vez de lançar erro para melhor resiliência
    return [];
  }
};

/**
 * Obtém informações sobre os regimes de mercado
 * @param {boolean} forceRefresh - Se deve forçar atualização dos dados
 * @returns {Promise<object>} Informações sobre regimes
 */
export const getRegimeInfo = async (forceRefresh = false) => {
  try {
    const { data } = await api.get(`/regimes/?force_refresh=${forceRefresh}`);
    return data;
  } catch (error) {
    console.error('Error fetching regime info:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao obter informações de regimes'));
  }
};

/**
 * Calcula o RSI atual com o período especificado
 * @param {number} period - Período para cálculo do RSI
 * @returns {Promise<object>} RSI atual
 */
export const calculateRSI = async (period = 14) => {
  try {
    const { data } = await api.get(`/rsi/current/?period=${period}`);
    return data;
  } catch (error) {
    console.error('Error calculating current RSI:', error);
    throw new Error(error.response?.data?.detail || 'Erro ao calcular RSI atual');
  }
};

/**
 * Atualiza o cache e o arquivo CSV de RSI
 * @param {string} csvPath - Caminho para o arquivo CSV (opcional)
 * @returns {Promise<object>} Resultado da atualização
 */
export const updateRSICache = async (csvPath = null) => {
  try {
    const url = csvPath ? `/rsi/update-cache/?csv_path=${encodeURIComponent(csvPath)}` : '/rsi/update-cache/';
    const { data } = await api.post(url);
    return data;
  } catch (error) {
    console.error('Error updating RSI cache:', error);
    throw new Error(error.response?.data?.detail || 'Erro ao atualizar cache de RSI');
  }
};

/**
 * Atualiza a notificação de regime para o usuário
 * @param {string} username - Nome do usuário
 * @param {string} regime - Regime a ser notificado
 * @returns {Promise<object>} Resultado da atualização
 */
export const updateRegimeNotification = async (username, regime) => {
  try {
    const { data } = await api.post(`/regimes/notification/${username}/`, { regime });
    return data;
  } catch (error) {
    console.error('Error updating regime notification:', error);
    throw new Error(error.response?.data?.detail || translateError('Erro ao atualizar notificação de regime'));
  }
}; 